import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle, Pause, Play, Sliders, Zap, XCircle, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useEngine, useConnection } from '../context/MarketContext';
import {
  fetchControlConfig, postControlConfig,
  fetchCommands, issueCommand, previewCommand,
} from '../api/control';
import { INSTRUMENTS } from '../constants/instruments';
import type { ControlConfigState, CommandRecord, Alert, ControlCommand } from '../types/control';
import { TIF_OPTIONS } from '../types/orders';
import { colors } from '../styles/tokens';
import { Panel } from '../components/ui';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function StatusPill({ label, active, color }: { label: string; active: boolean; color: string }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 700,
      backgroundColor: active ? `${color}22` : colors.bg.secondary,
      color: active ? color : '#46525e',
      border: `1px solid ${active ? color + '55' : colors.border.default}`,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
    }}>
      {label}
    </span>
  );
}

function Row({ children, gap = 12 }: { children: React.ReactNode; gap?: number }) {
  return <div style={{ display: 'flex', flexWrap: 'wrap', gap, alignItems: 'center' }}>{children}</div>;
}

function Btn({
  label, icon: Icon, onClick, variant = 'default', disabled = false, loading = false, width,
}: {
  label: string; icon?: React.ElementType; onClick: () => void;
  variant?: 'default' | 'primary' | 'danger' | 'warn' | 'success';
  disabled?: boolean; loading?: boolean; width?: number;
}) {
  const btnColors: Record<string, { bg: string; fg: string; border: string }> = {
    default:  { bg: colors.bg.secondary, fg: colors.text.secondary, border: colors.border.default },
    primary:  { bg: '#1d4ed8', fg: '#fff',    border: '#4da3ff' },
    danger:   { bg: '#7f1d1d', fg: '#fca5a5', border: '#ff5c5c' },
    warn:     { bg: '#78350f', fg: '#fcd34d', border: '#ff8c3a' },
    success:  { bg: '#14532d', fg: '#86efac', border: '#22c55e' },
  };
  const c = btnColors[variant];
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '8px 14px', borderRadius: 6,
        background: c.bg, color: c.fg, border: `1px solid ${c.border}`,
        fontSize: 13, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1, transition: 'all 0.15s',
        width: width ? width : undefined,
      }}
    >
      {Icon && <Icon size={14} />}
      {loading ? 'Working…' : label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Spread row per symbol
// ---------------------------------------------------------------------------

function SpreadRow({
  sym, bid, ask, onApply,
}: { sym: string; bid: number; ask: number; onApply: (sym: string, bid: number, ask: number) => void }) {
  const [localBid, setLocalBid] = useState(String(bid));
  const [localAsk, setLocalAsk] = useState(String(ask));

  useEffect(() => { setLocalBid(String(bid)); }, [bid]);
  useEffect(() => { setLocalAsk(String(ask)); }, [ask]);

  const input = (val: string, set: (v: string) => void) => (
    <input
      type="number" min={1} max={500} step={1}
      value={val}
      onChange={e => set(e.target.value)}
      style={{
        width: 70, padding: '5px 8px', borderRadius: 6,
        background: '#10151b', color: '#d7dde4', border: '1px solid #2a3540',
        fontSize: 13, outline: 'none',
      }}
    />
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: `1px solid ${colors.border.default}` }}>
      <span style={{ width: 80, fontSize: 13, fontWeight: 600, color: '#7e8a96' }}>{sym}</span>
      <span style={{ fontSize: 11, color: '#7a8693' }}>BID</span>
      {input(localBid, setLocalBid)}
      <span style={{ fontSize: 11, color: '#7a8693' }}>ASK</span>
      {input(localAsk, setLocalAsk)}
      <span style={{ fontSize: 11, color: '#7a8693' }}>bps</span>
      <Btn
        label="Apply"
        variant="primary"
        onClick={() => onApply(sym, Number(localBid), Number(localAsk))}
        disabled={!localBid || !localAsk || Number(localBid) <= 0 || Number(localAsk) <= 0}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Alert banner
// ---------------------------------------------------------------------------

function AlertBanner({ alerts, onDismiss }: { alerts: Alert[]; onDismiss: (id: string) => void }) {
  if (alerts.length === 0) return null;
  const latest = alerts.slice(0, 5);
  return (
    <div style={{ marginBottom: 16 }}>
      {latest.map(a => (
        <div key={a.id} style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px',
          borderRadius: 8, marginBottom: 6,
          background: a.severity === 'CRIT' ? '#450a0a' : '#451a03',
          border: `1px solid ${a.severity === 'CRIT' ? '#ff5c5c' : '#ff8c3a'}`,
        }}>
          <AlertTriangle size={15} color={a.severity === 'CRIT' ? '#ff5c5c' : '#ff8c3a'} style={{ marginTop: 1, flexShrink: 0 }} />
          <div style={{ flex: 1, fontSize: 13, color: '#d7dde4' }}>{a.message}</div>
          <button onClick={() => onDismiss(a.id)} style={{ background: 'none', border: 'none', color: '#7a8693', cursor: 'pointer', padding: 0 }}>
            <XCircle size={14} />
          </button>
        </div>
      ))}
      {alerts.length > 5 && (
        <div style={{ fontSize: 12, color: '#7a8693', paddingLeft: 4 }}>+{alerts.length - 5} more alerts</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Confirm modal
// ---------------------------------------------------------------------------

function ConfirmModal({
  token, preview, command, onConfirm, onCancel,
}: { token: string; preview: string; command: string; onConfirm: (token: string) => void; onCancel: () => void }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{
        background: colors.bg.primary, border: '1px solid #7f1d1d', borderRadius: 12,
        padding: 28, maxWidth: 420, width: '90%',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <AlertTriangle size={20} color="#ff5c5c" />
          <span style={{ fontSize: 16, fontWeight: 700, color: '#fca5a5' }}>Confirm: {command}</span>
        </div>
        <p style={{ fontSize: 13, color: '#7e8a96', marginBottom: 20, lineHeight: 1.6 }}>{preview}</p>
        <div style={{ fontSize: 11, color: '#7a8693', marginBottom: 20 }}>
          Token: <code style={{ color: '#ff8c3a' }}>{token}</code>
        </div>
        <Row>
          <Btn label="Cancel" onClick={onCancel} />
          <Btn label="Confirm — execute" variant="danger" onClick={() => onConfirm(token)} />
        </Row>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function ControlsPage() {
  const { token } = useAuth();
  const { engineConfig } = useEngine();
  const { connectionStatus } = useConnection();

  const [config, setConfig] = useState<ControlConfigState | null>(null);
  const [commands, setCommands] = useState<CommandRecord[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState<{ msg: string; ok: boolean } | null>(null);
  const [showCommands, setShowCommands] = useState(false);
  // Local state for default TIF expiry fields (GTD/GTT mode)
  const [defExpDate, setDefExpDate] = useState('');
  const [defExpTime, setDefExpTime] = useState('');
  const [confirmPending, setConfirmPending] = useState<{ token: string; preview: string; command: ControlCommand } | null>(null);

  const feedbackTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const _feedback = useCallback((msg: string, ok: boolean) => {
    setFeedback({ msg, ok });
    clearTimeout(feedbackTimer.current);
    feedbackTimer.current = setTimeout(() => setFeedback(null), 4000);
  }, []);

  // Load config + commands on mount
  useEffect(() => {
    if (!token) return;
    fetchControlConfig(token)
      .then(cfg => {
        setConfig(cfg);
        if (cfg.default_expire_date) {
          const d = cfg.default_expire_date;
          setDefExpDate(`${d.slice(0,4)}-${d.slice(4,6)}-${d.slice(6,8)}`);
        }
        if (cfg.default_expire_time) setDefExpTime(cfg.default_expire_time.slice(0,5));
      })
      .catch(e => _feedback(String(e), false));
    fetchCommands(token)
      .then(setCommands)
      .catch(() => {});
  }, [token, _feedback]);

  // Merge live WS engineConfig into local state (so page shows live mode even without REST round-trip)
  useEffect(() => {
    if (!engineConfig) return;
    setConfig(prev => prev ? {
      ...prev,
      market_making_enabled: engineConfig.market_making_enabled,
      market_making_mode: engineConfig.market_making_mode,
      ml_enabled: engineConfig.ml_enabled,
    } : prev);
  }, [engineConfig]);

  // ---------------------------------------------------------------------------
  // Config helpers
  // ---------------------------------------------------------------------------

  const postConfig = useCallback(async (fields: Record<string, unknown>, reason?: string) => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await postControlConfig(token, fields, reason);
      if (res.ok) {
        setConfig(prev => prev ? { ...prev, ...fields } as ControlConfigState : prev);
        _feedback(`Updated: ${res.written.join(', ')}`, true);
      } else {
        _feedback(res.errors.map(e => e.error).join('; '), false);
      }
    } catch (e: any) {
      _feedback(String(e?.message ?? e), false);
    } finally {
      setLoading(false);
    }
  }, [token, _feedback]);

  // ---------------------------------------------------------------------------
  // Command helpers
  // ---------------------------------------------------------------------------

  const sendCommand = useCallback(async (command: ControlCommand, args?: Record<string, unknown>, confirmToken?: string) => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await issueCommand(token, command, args, confirmToken);
      _feedback(`Command sent: ${command} (${res.status})`, true);
      // Refresh command list
      fetchCommands(token).then(setCommands).catch(() => {});
    } catch (e: any) {
      if (e.message === 'requires_confirm' && e.confirmData) {
        const cd = e.confirmData;
        setConfirmPending({ token: cd.confirm_token, preview: cd.preview, command });
      } else {
        _feedback(String(e?.message ?? e), false);
      }
    } finally {
      setLoading(false);
    }
  }, [token, _feedback]);

  const onConfirmModal = useCallback(async (tok: string) => {
    if (!confirmPending) return;
    setConfirmPending(null);
    await sendCommand(confirmPending.command, {}, tok);
  }, [confirmPending, sendCommand]);

  const handleSpreadApply = useCallback((sym: string, bid: number, ask: number) => {
    postConfig({ spread_bps_bid: { [sym]: bid }, spread_bps_ask: { [sym]: ask } }, `Spread update for ${sym}`);
  }, [postConfig]);

  if (!config) {
    return (
      <div style={{ padding: 32, color: '#7a8693', fontSize: 14 }}>
        Loading control config…
      </div>
    );
  }

  const isRunning = config.market_making_enabled && !config.quoting_paused;
  const isPaused  = config.quoting_paused;
  const mode      = config.market_making_mode;

  return (
    <div style={{ padding: '20px 24px', width: '100%', background: colors.bg.primary, minHeight: '100vh', boxSizing: 'border-box' }}>

      {/* Alerts */}
      <AlertBanner alerts={alerts} onDismiss={id => setAlerts(a => a.filter(x => x.id !== id))} />

      {/* Confirm modal */}
      {confirmPending && (
        <ConfirmModal
          token={confirmPending.token}
          preview={confirmPending.preview}
          command={confirmPending.command}
          onConfirm={onConfirmModal}
          onCancel={() => setConfirmPending(null)}
        />
      )}

      {/* Feedback */}
      {feedback && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 8, marginBottom: 14,
          background: feedback.ok ? '#14532d33' : '#7f1d1d33',
          border: `1px solid ${feedback.ok ? '#22c55e55' : '#ff5c5c55'}`,
        }}>
          {feedback.ok ? <CheckCircle size={14} color="#22c55e" /> : <AlertTriangle size={14} color="#ff5c5c" />}
          <span style={{ fontSize: 13, color: feedback.ok ? '#86efac' : '#fca5a5' }}>{feedback.msg}</span>
        </div>
      )}

      {/* ── Status bar ── */}
      <Panel
        title="System status"
        badge={isRunning ? 'Quoting' : isPaused ? 'Paused' : 'Stopped'}
        badgeTone={isRunning ? 'ok' : 'warn'}
        style={{ marginBottom: 16 }}
      >
        <Row gap={10}>
          <StatusPill label={isRunning ? 'Quoting' : isPaused ? 'Paused' : 'Stopped'} active={isRunning} color="#22c55e" />
          <StatusPill label={mode === 'automated' ? 'Automated' : 'Manual'} active color={mode === 'automated' ? '#4da3ff' : '#a78bfa'} />
          <StatusPill label={config.ml_enabled ? 'ML on' : 'ML off'} active={config.ml_enabled} color="#3ec9d6" />
          <StatusPill label={connectionStatus === 'connected' ? 'WS live' : 'WS offline'} active={connectionStatus === 'connected'} color="#22c55e" />
        </Row>
      </Panel>

      {/* ── Master controls ── */}
      <Panel title="Master controls" style={{ marginBottom: 16 }}>
        <Row>
          {!isRunning && (
            <Btn
              label="Enable MM"
              icon={Play}
              variant="success"
              loading={loading}
              onClick={() => postConfig({ market_making_enabled: true, quoting_paused: false })}
            />
          )}
          {isRunning && (
            <Btn
              label="Pause quoting"
              icon={Pause}
              variant="warn"
              loading={loading}
              onClick={() => sendCommand('pause_quoting')}
            />
          )}
          {isPaused && (
            <Btn
              label="Resume quoting"
              icon={Play}
              variant="success"
              loading={loading}
              onClick={() => sendCommand('resume_quoting')}
            />
          )}
          <Btn
            label="Cancel all orders"
            icon={XCircle}
            variant="warn"
            loading={loading}
            onClick={() => sendCommand('cancel_all')}
          />
          <Btn
            label="KILL SWITCH"
            icon={Zap}
            variant="danger"
            loading={loading}
            onClick={() => sendCommand('kill_switch')}
          />
        </Row>
      </Panel>

      {/* ── Mode ── */}
      <Panel title="Trading mode" badge={mode} style={{ marginBottom: 16 }}>
        <Row>
          <Btn
            label="Manual (suggest + approve)"
            icon={Sliders}
            variant={mode === 'manual' ? 'primary' : 'default'}
            loading={loading}
            onClick={() => postConfig({ market_making_mode: 'manual' }, 'mode switch')}
          />
          <Btn
            label="Automated (auto-place)"
            icon={Zap}
            variant={mode === 'automated' ? 'primary' : 'default'}
            loading={loading}
            onClick={() => postConfig({ market_making_mode: 'automated' }, 'mode switch')}
          />
        </Row>
        <p style={{ fontSize: 12, color: '#46525e', marginTop: 10 }}>
          Manual mode: engine suggests quotes; you approve/reject each one. Automated: engine places orders directly.
        </p>
      </Panel>

      {/* ── Spreads ── */}
      <Panel title="Spread control (bps)" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {INSTRUMENTS.map(({ label }) => (
            <SpreadRow
              key={label}
              sym={label}
              bid={config.spread_bps_bid[label] ?? 50}
              ask={config.spread_bps_ask[label] ?? 50}
              onApply={handleSpreadApply}
            />
          ))}
        </div>
        <p style={{ fontSize: 12, color: '#46525e', marginTop: 12 }}>
          Widen spreads to reduce fill rate / accumulation. Tighten to be more aggressive. Applies on next quote cycle.
        </p>
      </Panel>

      {/* ── Inventory targets ── */}
      <Panel title="Inventory targets (shares)" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {INSTRUMENTS.map(({ label }) => {
            const cur = config.inventory_target[label];
            return (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ width: 80, fontSize: 13, color: '#7e8a96', fontWeight: 600 }}>{label}</span>
                <InventoryInput
                  sym={label}
                  value={cur ?? 0}
                  onApply={(sym, v) => postConfig({ inventory_target: { [sym]: v } }, `Inventory target ${sym}`)}
                />
              </div>
            );
          })}
        </div>
        <p style={{ fontSize: 12, color: '#46525e', marginTop: 12 }}>
          Set target net inventory (positive = net long, negative = net short, 0 = flat). Engine biases quotes toward target.
        </p>
      </Panel>

      {/* ── ML ── */}
      <Panel
        title="ML & model"
        badge={config.ml_enabled ? 'On' : 'Off'}
        badgeTone={config.ml_enabled ? 'ok' : 'default'}
        style={{ marginBottom: 16 }}
      >
        <Row>
          <Btn
            label={config.ml_enabled ? 'Disable ML' : 'Enable ML'}
            variant={config.ml_enabled ? 'warn' : 'success'}
            loading={loading}
            onClick={() => postConfig({ ml_enabled: !config.ml_enabled })}
          />
        </Row>
      </Panel>

      {/* ── Default Time In Force ── */}
      <Panel title="Default time in force" style={{ marginBottom: 16 }}>
        <p style={{ fontSize: 12, color: '#46525e', marginTop: 0, marginBottom: 10 }}>
          Applies to all new orders (manual + automated) unless overridden per-order.
        </p>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          {TIF_OPTIONS.map(({ v, label, desc }) => (
            <button
              key={v}
              disabled={loading}
              title={desc}
              onClick={() => postConfig({ default_time_in_force: v }, `Default TIF -> ${label}`)}
              style={{
                padding: '5px 14px', fontSize: 11, fontWeight: 700, borderRadius: 4,
                cursor: loading ? 'not-allowed' : 'pointer', border: 'none',
                backgroundColor: (config.default_time_in_force ?? 1) === v ? '#4da3ff' : '#2a3540',
                color: (config.default_time_in_force ?? 1) === v ? 'white' : '#7a8693',
              }}
            >
              {label}
            </button>
          ))}
        </div>
        {(config.default_time_in_force ?? 1) === 6 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, alignItems: 'flex-end' }}>
            <div>
              <label style={{ fontSize: 10, color: '#46525e', fontWeight: 700, display: 'block', marginBottom: 4 }}>DEFAULT EXPIRE DATE</label>
              <input
                type="date"
                value={defExpDate}
                onChange={e => setDefExpDate(e.target.value)}
                style={{
                  width: '100%', padding: '6px 10px', background: colors.bg.primary, color: colors.text.primary,
                  border: `1px solid ${colors.border.default}`, borderRadius: 4, fontSize: 12, boxSizing: 'border-box',
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: 10, color: '#46525e', fontWeight: 700, display: 'block', marginBottom: 4 }}>DEFAULT EXPIRE TIME (EAT)</label>
              <input
                type="time"
                value={defExpTime}
                onChange={e => setDefExpTime(e.target.value)}
                style={{
                  width: '100%', padding: '6px 10px', background: colors.bg.primary, color: colors.text.primary,
                  border: `1px solid ${colors.border.default}`, borderRadius: 4, fontSize: 12, boxSizing: 'border-box',
                }}
              />
            </div>
            <button
              disabled={loading}
              onClick={() => postConfig({
                default_expire_date: defExpDate ? defExpDate.replace(/-/g, '') : '',
                default_expire_time: defExpTime ? `${defExpTime}:00` : '',
              }, 'Default GTD expiry')}
              style={{
                padding: '6px 14px', fontSize: 11, fontWeight: 700, borderRadius: 4,
                cursor: loading ? 'not-allowed' : 'pointer', border: 'none',
                backgroundColor: '#4da3ff', color: 'white',
              }}
            >
              Save
            </button>
          </div>
        )}
      </Panel>

      {/* ── Flatten ── */}
      <Panel title="Flatten positions" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {INSTRUMENTS.map(({ label }) => (
            <Btn
              key={label}
              label={`Flatten ${label}`}
              icon={RotateCcw}
              variant="warn"
              loading={loading}
              onClick={() => sendCommand('flatten', { symbol: label, style: 'laddered' })}
            />
          ))}
          <Btn
            label="Flatten ALL"
            icon={RotateCcw}
            variant="danger"
            loading={loading}
            onClick={() => sendCommand('flatten', { symbol: 'all', style: 'laddered' })}
          />
        </div>
        <p style={{ fontSize: 12, color: '#46525e', marginTop: 12 }}>
          Flatten submits laddered limit orders to reduce position to zero. Destructive — requires confirm.
        </p>
      </Panel>

      {/* ── Recent commands ── */}
      <Panel title="Recent commands" badge={commands.length} style={{ marginBottom: 16 }}>
        <button
          onClick={() => setShowCommands(v => !v)}
          style={{ background: 'none', border: 'none', color: '#7a8693', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, marginBottom: showCommands ? 12 : 0 }}
        >
          {showCommands ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          {showCommands ? 'Hide' : 'Show'}
        </button>
        {showCommands && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ color: '#46525e', textAlign: 'left' }}>
                  {['Time', 'Command', 'Args', 'By', 'Status'].map(h => (
                    <th key={h} style={{ padding: '4px 8px', borderBottom: `1px solid ${colors.border.default}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {commands.slice(0, 20).map(cmd => (
                  <tr key={cmd.id} style={{ borderBottom: `1px solid ${colors.bg.primary}` }}>
                    <td style={{ padding: '5px 8px', color: '#7a8693' }}>
                      {cmd.issued_at ? new Date(cmd.issued_at).toLocaleTimeString() : '—'}
                    </td>
                    <td style={{ padding: '5px 8px', color: '#d7dde4', fontWeight: 600 }}>{cmd.command}</td>
                    <td style={{ padding: '5px 8px', color: '#7e8a96' }}>
                      {cmd.args ? JSON.stringify(cmd.args) : '—'}
                    </td>
                    <td style={{ padding: '5px 8px', color: '#7a8693' }}>{cmd.issued_by}</td>
                    <td style={{ padding: '5px 8px' }}>
                      <span style={{ color: cmd.status === 'applied' ? '#22c55e' : cmd.status === 'failed' ? '#ff5c5c' : '#ff8c3a' }}>
                        {cmd.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {commands.length === 0 && (
                  <tr><td colSpan={5} style={{ padding: 12, color: '#46525e', textAlign: 'center' }}>No commands yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inventory input sub-component
// ---------------------------------------------------------------------------

function InventoryInput({ sym, value, onApply }: { sym: string; value: number; onApply: (sym: string, v: number) => void }) {
  const [local, setLocal] = useState(String(value));
  useEffect(() => { setLocal(String(value)); }, [value]);
  return (
    <>
      <input
        type="number" step={100}
        value={local}
        onChange={e => setLocal(e.target.value)}
        style={{
          width: 90, padding: '5px 8px', borderRadius: 6,
          background: '#10151b', color: '#d7dde4', border: '1px solid #2a3540',
          fontSize: 13, outline: 'none',
        }}
      />
      <span style={{ fontSize: 11, color: '#7a8693' }}>shares</span>
      <Btn label="Set" variant="primary" onClick={() => onApply(sym, Number(local))} />
    </>
  );
}
